// Globals
import React, { useCallback, useEffect, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import Slider from 'infinite-react-carousel';

// Locals
import {
  closeRecordingErrorModal,
  closeRecordingProcessingModal,
  setGiideContentModal,
  setGiidePasswordModal,
  setGiideTabState,
  setPlayingProgress,
  setRecordingErrorModalVisible,
  setTranscriptData,
  unlinkRecordingLocal,
} from '../../actions';
import useGiide from '../../hooks/useQuery/useGiide';
import styles from '../Components.module.css';
import AudioTab from './AudioTab';
import StepTranscript from './StepTranscript';
import { deleteKey as cacheDeleteKey } from '../../components/cache';

import type { AudioLibOperationsHook, GiideRouteParams } from '../../types';
import { GiidePublishStateObj, giideTabState } from '../../types';
import { IState, PasswordModalVariants } from '../../graphql/types';
import { Button, Flex, Modal, Steps } from 'antd';
import { CreationStepsArray as stepArray } from '../utils';
import StepPublished from './StepPublished';
import useAudioLibraryOperations from '../../hooks/useAudioLibraryOperations';

import image1 from '../../studio/assets/transcribe_slide_1.png';
import image2 from '../../studio/assets/transcribe_slide_2.png';
import image3 from '../../studio/assets/transcribe_slide_3.png';
import image4 from '../../studio/assets/transcribe_slide_4.png';
import image5 from '../../studio/assets/transcribe_slide_5.png';
import image6 from '../../studio/assets/transcribe_slide_6.png';
import useMutation from '../../hooks/useMutation';
import {
  UnlinkRecordingInput,
  UnlinkRecordingMutation,
} from '../../graphql/api';
import { unlinkRecording } from '../../graphql/mutations';
import { ModalContent } from '../../hooks/useGiideContentModal';
import PublishModal from '../Publish/Publish';
import useUser from '../../hooks/useUser';

import { BouncingBalls } from '../Spinners/BouncingBalls';

const { Step } = Steps;

const GiideWithStep: React.FC<{ hasChapters: boolean }> = ({ hasChapters }) => {
  const dispatch = useDispatch();
  const tabState = useSelector<IState, giideTabState>(state => state.tabState);
  const [publishing, setPublishing] = useState<GiidePublishStateObj>({});

  const recordingErrorModalVisible = useSelector<IState, boolean>(
    state => state.recordingErrorModalVisible || false
  );
  const recordingProcessingModalVisible = useSelector<IState, boolean>(
    state => !!state.recordingProcessingModalVisible
  );

  const { user } = useUser();

  const unlinkRecordingMutation = useMutation<
    UnlinkRecordingInput,
    UnlinkRecordingMutation
  >(unlinkRecording);

  const { push } = useHistory();
  const { id } = useParams<GiideRouteParams>();
  if (!id) {
    push('/');
  }
  const { openedStep, data } = useGiide(id);

  const unlockedGiides = useSelector<IState, string[]>(
    state => state.unlockedGiides
  );

  useEffect(() => {
    if (data?.passwordEnabled === true && !unlockedGiides.includes(id)) {
      push('/');
      dispatch(setGiidePasswordModal(id, PasswordModalVariants.Check));
    }
  }, [unlockedGiides, id, data, dispatch, push]);

  const { audioLibOperations, audioLibOperationsData }: AudioLibOperationsHook =
    useAudioLibraryOperations(openedStep?.id, data?.id);

  const [sliderVisible, setSliderVisible] = React.useState<boolean>(false);

  // solve for weird issue with react-infinite-carousel library
  // that causes app to crash if visible when app loads
  React.useEffect(() => {
    setSliderVisible(true);
  }, []);

  useEffect(() => {
    if (!tabState) {
      if (
        !openedStep?.recording?.deleted &&
        openedStep?.recording?.filePath &&
        data // &&
        // data.publishState !== 'published'
      ) {
        dispatch(setGiideTabState('transcript'));
      } else {
        dispatch(setGiideTabState('audio'));
      }
    }
  }, [data, openedStep, dispatch, tabState]);

  const handleStepClick = useCallback(
    step => {
      if (typeof step === 'number') {
        if (step === 0) {
          step = 'Add Audio';
        }

        if (step === 1) {
          step = 'Create Feed';
        }

        if (step === 2) {
          step = 'Share';
        }
      }

      if (step === 'Add Audio' && tabState !== 'audio') {
        dispatch(setGiideTabState('audio'));
      } else if (
        step === 'Create Feed' &&
        !openedStep?.recording?.deleted &&
        openedStep?.recording?.filePath &&
        openedStep?.recording?.transcriptResults &&
        tabState !== 'transcript'
      ) {
        batch(() => {
          // To prevent flash of content before reloading
          dispatch(setTranscriptData(undefined));
          dispatch(setGiideTabState('transcript'));
        });
      } else if (
        step === 'Share' &&
        data?.publishState === 'published' &&
        tabState !== 'published'
      ) {
        dispatch(setGiideTabState('published'));
      }
    },
    [dispatch, openedStep, tabState, data?.publishState]
  );

  const isStepDisabled = useCallback(
    step => {
      if (
        step === 'Create Feed' &&
        !(
          !openedStep?.recording?.deleted &&
          openedStep?.recording?.filePath &&
          openedStep?.recording?.transcriptResults
        )
      ) {
        return true;
      } else if (step === 'Share' && !(data?.publishState === 'published')) {
        return true;
      }
      return false;
    },
    [openedStep, data?.publishState]
  );

  const settings = {
    slidesToShow: 1,
    arrows: false,
    autoplay: true,
    autoplaySpeed: 7000,
    dots: true,
  };

  const getTranscribeEstimate = (audioLength: number) => {
    if (audioLength <= 60) return 'about 30 seconds';
    else if (audioLength <= 180) return 'less than one minute';
    else if (audioLength <= 240) return 'about one minute';
    else if (audioLength <= 480) return 'about one and a half minutes';
    else if (audioLength <= 720) return 'about two minutes';
    else if (audioLength <= 900) return 'about two and half minutes';
    else return 'about 3 minutes';
  };

  const modalSlides = [
    {
      text: 'Hold tight! Your recording is now transcribing.',
      image: image1,
      altText: 'clock',
    },
    {
      text: 'Once you get your transcription, you might notice typos. You can fix these by clicking the edit transcription button.',
      image: image2,
      altText: 'piece of paper will stars on it',
    },
    {
      text: 'We only use the transcription to ensure that the feedcards you add appear exactly when you want them to.',
      image: image3,
      altText: 'clapperboard',
    },
    {
      text: 'To add a feedcard, simply click on any word in your transcription.',
      image: image4,
      altText: 'keyboard',
    },
    {
      text: 'Feedcards allow you to provide more context for your listeners, and are great for encouraging exploration.',
      image: image5,
      altText: 'molecule',
    },
    {
      text: 'Did you mention a book? Link to it! Were you talking about a place? Add a picture! You get the point.',
      image: image6,
      altText: 'sign saying "check it out!"',
    },
  ];

  useEffect(() => {
    if (openedStep?.recording && openedStep?.recording?.transcriptError) {
      dispatch(setRecordingErrorModalVisible(true));
    }
  }, [dispatch, openedStep]);

  const closeErrorDlg = useCallback(async () => {
    if (openedStep?.recording) {
      const timestamp = new Date().toISOString();
      const unlinkProps = { id: openedStep.recording.id, timestamp };
      await unlinkRecordingMutation.callMutation(unlinkProps);
      dispatch(unlinkRecordingLocal(unlinkProps));
      cacheDeleteKey(`public/${openedStep?.recording.filePath}-get`);
      cacheDeleteKey(`${openedStep?.recording.transcriptPath}-get`);
    }
    batch(() => {
      dispatch(setPlayingProgress(0));
      dispatch(closeRecordingErrorModal());
      dispatch(setGiideTabState('audio'));
    });
  }, [openedStep, dispatch, unlinkRecordingMutation]);

  const giideContentModal = useSelector<IState, ModalContent>(
    state => state.giideContentModal
  );

  const handleCloseModal = () =>
    dispatch(setGiideContentModal(ModalContent.none));

  return (
    <div
      className={'giide-steps-container'}
      style={{ paddingRight: tabState === 'published' ? 165 : 0 }}
    >
      <Modal
        destroyOnClose
        title={null}
        open={recordingErrorModalVisible}
        footer={null}
        closable={false}
        centered
        className={styles.transcriptionErrorModal}
        styles={{ body: { padding: 0 } }}
      >
        <div className={styles.transcriptionErrorModalWrapper}>
          <h2>Oh no! We’re not detecting any audio!</h2>
          <p>This could be because:</p>
          <ul>
            <li>Your mic isn't turned on or set up properly</li>
            <li>
              You haven't spoken long enough (we need at least a sentence or two
              for transcription to work)
            </li>
            <li>
              You are trying to upload a song, which unfortunately doesn't work!
            </li>
          </ul>

          <Button
            className={styles.transcriptionErrorBtn}
            onClick={closeErrorDlg}
          >
            Return to recording screen
          </Button>
        </div>
      </Modal>
      <Modal
        destroyOnClose
        title={null}
        open={
          !recordingErrorModalVisible &&
          (recordingProcessingModalVisible ||
            (openedStep?.recording &&
              !openedStep?.recording?.transcriptResults &&
              !openedStep?.recording?.deleted))
        }
        footer={null}
        closable={false}
        onCancel={() => dispatch(closeRecordingProcessingModal())}
      >
        <div className={styles.transcriptionProcessingModalWrapper}>
          <BouncingBalls />

          {sliderVisible && (
            <Slider
              className={`${styles.slideCarousel} slideCarousel`}
              {...settings}
            >
              {modalSlides.map((slide, i) => (
                <div className={styles.modalSlide} key={slide.text}>
                  <p>
                    {i === 0 &&
                    openedStep?.recording &&
                    openedStep?.recording.length
                      ? `Hold tight! Your recording is now transcribing. This should
                          take ${getTranscribeEstimate(
                            openedStep?.recording!.length! / 1000
                          )}.`
                      : slide.text}
                  </p>
                </div>
              ))}
            </Slider>
          )}
        </div>
      </Modal>

      {data && user && (
        <PublishModal
          giide={data}
          tenantId={user.tenantID}
          userId={user.userId}
          closeModal={handleCloseModal}
          open={giideContentModal === ModalContent.publish}
          publishing={publishing}
          setPublishing={setPublishing}
        />
      )}

      <Steps
        style={{
          width: '538px',
          margin: '32px auto 0',
        }}
        current={
          tabState === 'published' ? 2 : tabState === 'transcript' ? 1 : 0
        }
        onChange={handleStepClick}
        labelPlacement={'vertical'}
        size="small"
      >
        {stepArray.map(item => {
          const stepDisabled = isStepDisabled(item.title);

          return (
            <Step
              key={item.title}
              onClick={
                !stepDisabled ? () => handleStepClick(item.title) : undefined
              }
              disabled={stepDisabled}
              title={
                <Flex gap={'small'} style={{ cursor: 'pointer' }}>
                  {item.title}
                </Flex>
              }
            />
          );
        })}
      </Steps>
      <div className={styles.tabsWrapper}>
        {tabState === 'audio' && (
          <AudioTab
            giideId={id}
            audioLibOperations={audioLibOperations}
            audioLibOperationsData={audioLibOperationsData}
          />
        )}
        {tabState === 'transcript' && <StepTranscript />}
        {tabState === 'published' && (
          <StepPublished
            giideId={data!.id}
            giideTitle={data!.title}
            giideEmbedCodeState={data!.embedCodeState}
            giideEmbedCodeAllowedDomains={data!.embedCodeAllowedDomains}
            giideAuthor={data?.speaker?.fullName}
            exportState={data?.exportVideoState}
          />
        )}
      </div>
    </div>
  );
};

export default GiideWithStep;
